import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { api, ApiError } from "@/lib/api";
import { JobsTable } from "@/components/JobsTable";
import { normalizeJobStatus } from "@/lib/jobStatus";
import { useAuthToken } from "@/lib/auth";
import { PublishActivityCard } from "@/components/PublishActivityCard";

/** Mirrors private/auth's jobsAdminRoutes.ts AdminJobRow (the trimmed
 * list-view projection — GET /v1/admin/jobs), not the full row. */
interface JobListRow {
  id: string;
  org_id: string;
  /** Resolved from pure_app's orgs table — null if that lookup itself
   *  failed (see jobsAdminRoutes.ts's lookupJobIdentities), fall back to
   *  the raw id in that case. */
  org_name: string | null;
  /** The creator of the API key the job was submitted with — see
   *  jobsAdminRoutes.ts's lookupJobIdentities header for why that's what
   *  "ran it" means here, not a true per-invocation actor. */
  user_name: string | null;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  /** Set when the job's artifacts were deleted with a published build; the
   *  broker serves such a job as status "deleted" and the page normalizes it
   *  back to the stored outcome (lib/jobStatus.ts). */
  outcome?: string | null;
  artifacts_deleted_at?: number | null;
  entry: string;
  /** What kind of build it is: the submitter's self-description
   *  (internal/api migration 0015, pure-publish) and the columns a label
   *  can be derived from — lib/buildLabels.ts. */
  target: string | null;
  libc: string | null;
  platform: string | null;
  type_profile_collect: number | null;
  pgo_generate: number | null;
  pgo_profile_path: string | null;
  build_kind: string | null;
  build_label: string | null;
  build_seq: number | null;
  build_run: string | null;
  /** Code metrics (internal/api migrations/0014_jobs_code_metrics.sql).
   *  Work units are internal-only and deliberately not shown here. */
  total_lines_of_code: number | null;
  compiled_lines_of_code: number | null;
  dynamic_code: number | null;
  binary_bytes: number | null;
  error_message: string | null;
  /** Failure verdict (internal/api migration 0018; lib/failureClass.ts). */
  failure_class?: string | null;
  failure_cause?: string | null;
  bundle_key?: string | null;
  failure_backfilled?: boolean;
  /** Warm-cache summary (internal/api migration 0019; lib/cacheVerdict.ts). */
  cold_unit_count?: number | null;
  unexpected_cold_count?: number | null;
  cold_top?: string | null;
  /** Peak guest memory, MiB (internal/api migration 0017); null = unknown. */
  mem_peak_mb?: number | null;
  created_at: number;
  started_at: number | null;
  finished_at: number | null;
  updated_at: number;
}

function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function LogsPage() {
  const token = useAuthToken();
  const navigate = useNavigate();
  const call = useCallback(
    <T,>(path: string, init?: RequestInit) => api<T>(token, path, init),
    [token],
  );

  const [jobs, setJobs] = useState<JobListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [lookupId, setLookupId] = useState("");
  const [lookupError, setLookupError] = useState<string | null>(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (orgFilter) params.set("org_id", orgFilter.trim());
    params.set("limit", "50");
    call<{ ok: true; jobs: JobListRow[] }>(`/admin/jobs?${params}`)
      .then((r) => setJobs(r.jobs.map(normalizeJobStatus)))
      .catch((err) => setError(errMsg(err, "failed to load jobs")));
  }, [call, statusFilter, orgFilter]);

  useEffect(load, [load]);

  // Live-ish list view: jobs move through queued -> running -> terminal on
  // their own, and there's no push channel for "a job changed" (see
  // LogJobDetailPage's own header for why the detail page polls directly
  // instead of extending JobEventsDO's customer-scoped socket) — a plain
  // refetch interval keeps this list current without any new backend work.
  useEffect(() => {
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [load]);

  function submitLookup(e: React.FormEvent) {
    e.preventDefault();
    const id = lookupId.trim();
    if (!id) return;
    setLookupError(null);
    call(`/admin/jobs/${encodeURIComponent(id)}`)
      .then(() => navigate(`/logs/${encodeURIComponent(id)}`))
      .catch((err) => setLookupError(errMsg(err, "job not found")));
  }

  return (
    <div className="max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Logs</h1>
        <p className="text-sm text-muted-foreground">
          Look up any compile job by id, or browse recent jobs across every org
          — no box-level SSH or journalctl needed.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Look up a job</CardTitle>
          <CardDescription>
            Paste a job id (e.g. from a customer report or a failed e2e run) to
            jump straight to its detail + live log.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitLookup} className="flex items-center gap-2">
            <Input
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              placeholder="job id — e.g. 25d9449f-8ec3-46ef-b0bb-79774fc0f36e"
              className="font-mono text-xs"
            />
            <Button type="submit">Open</Button>
          </form>
          {lookupError && (
            <p className="text-sm text-destructive mt-2">{lookupError}</p>
          )}
        </CardContent>
      </Card>

      <PublishActivityCard token={token} />

      <Card>
        <CardHeader>
          <CardTitle>Recent jobs</CardTitle>
          <CardDescription>Newest first, across every org.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-40"
            >
              <option value="">(any status)</option>
              <option value="queued">queued</option>
              <option value="running">running</option>
              <option value="succeeded">succeeded</option>
              <option value="failed">failed</option>
              <option value="cancelled">cancelled</option>
              <option value="deleted">artifacts deleted</option>
            </Select>
            <Input
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
              placeholder="filter by org id"
              className="w-56 font-mono text-xs"
            />
          </div>

          {error && <p className="text-sm text-destructive mb-2">{error}</p>}

          {jobs && jobs.length > 0 && <JobsTable jobs={jobs} />}
          {jobs?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No jobs match this filter.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
